import {
    DateTimeUtils,
    DeviceDetectionUtils,
    FrameCollectionModule,
    Logger,
    PermissionsManager,
    ReduxUtils,
} from 'UI-Dash-Commons-UI';
import { push } from 'connected-react-router';

import { focusSectionFrame } from '../../FrameCollection/redux/actions';
import {
    URL_TYPE_IFRAME,
    URL_TYPE_NEW_TAB,
    URL_TYPE_NEW_WINDOW,
    URL_TYPE_SAME_TAB,
    URL_TYPE_DEBUG_INFO,
    URL_TYPE_IFRAME_LOCATION_CHANGE,
    URL_TYPE_THIRD_PARTY_IFRAME,
    URL_TYPE_THIRD_PARTY_IFRAME_LOCATION_CHANGE,
} from '../../FrameCollection/redux/constants';
import MenuSection from '../../../types/MenuSection';
import MenuServiceProxy from '../../../utils/MenuServiceProxy';

import * as constants from './constants';
import MainNavigationState from "../types/MainNavigationState";

const TAG = 'MainNavigationActions';

export const onMenuSpecFetchSuccess = ReduxUtils.createAction( constants.ACTION_ON_MENU_SPEC_FETCH_SUCCESS, 'menuSpec' );
export const onMenuSpecFetchError = ReduxUtils.createAction( constants.ACTION_ON_MENU_SPEC_FETCH_ERROR, 'errorResponse' );
export const updateSideLeftMenuPinnedOpenState = ReduxUtils.createAction( constants.ACTION_UPDATE_SIDE_MENU_PINNED_OPEN_STATE, 'isPinnedOpen' );
export const updateSideLeftMenuOpenState = ReduxUtils.createAction( constants.ACTION_UPDATE_SIDE_MENU_OPEN_STATE, 'isOpen' );
export const updateSideLeftOpenSectionId = ReduxUtils.createAction( constants.ACTION_UPDATE_SIDE_OPEN_SECTION_ID, 'sectionId' );
export const updateDebugInfoOpenState = ReduxUtils.createAction( constants.ACTION_UPDATE_DEBUG_INFO_OPEN_STATE, 'isOpen' );

export const fetchMenuSpec = () => ( dispatch ) =>
{
    ( new MenuServiceProxy() ).fetchMenuSpec(
        ( response ) =>
        {
            updatePageTitle( response.data.leftSideMenu.activeSection );
            PermissionsManager.saveNewPermissions( response.data.permissions );

            dispatch( onMenuSpecFetchSuccess( response.data ) );
        },
        ( errorResponse ) => dispatch( onMenuSpecFetchError( errorResponse ) )
    );
};


const updatePageTitle = ( sectionSpec: MenuSection ) =>
{
    if ( sectionSpec && sectionSpec.pageHeader )
    {
        // Update window title
        const pageHeaderParts = sectionSpec.pageHeader.split(' - ');
        // Both parts being the same should only occur if the main section header is a route
        // For example, if you could click MAX without clicking a subsection, it would be MAX - MAX.
        if ( pageHeaderParts[0] === pageHeaderParts[1] ) {
            document.title = `AppLovin - ${pageHeaderParts[0]}`;
        } else {
            document.title = sectionSpec.pageHeader;
        }
    }
};

export const activateSectionWithIframeWindowLocation = ( sectionSpec: MenuSection, iframeSectionSpec: MenuSection, updateTimeStamp = true ) => ( dispatch, getState ) =>
{
    updatePageTitle( iframeSectionSpec );

    dispatch( activateLeftMenuSection( iframeSectionSpec, updateTimeStamp ) );

    dispatch( focusSectionFrame( sectionSpec, iframeSectionSpec ) );

    let messageType = URL_TYPE_IFRAME_LOCATION_CHANGE;

    if ( iframeSectionSpec.sectionUrlType === URL_TYPE_THIRD_PARTY_IFRAME )
    {
        messageType = URL_TYPE_THIRD_PARTY_IFRAME_LOCATION_CHANGE;

        activateSection( sectionSpec );
    }

    FrameCollectionModule.utils.DispatchFrameCollectionManagerUtils.locationChange(
        {
            url: iframeSectionSpec.sectionUrl,
            type: messageType,
        }
    );
};

export const activateSection = ( sectionSpec: MenuSection, pushUrl = true, updateTimeStamp = true ) => ( dispatch, getState ) =>
{
    Logger.info( TAG, 'Handling activation of: ' + JSON.stringify( sectionSpec ) + '...' );

    if ( sectionSpec.sectionUrlType === URL_TYPE_IFRAME || sectionSpec.sectionUrlType === URL_TYPE_THIRD_PARTY_IFRAME )
    {
        Logger.debug( TAG, 'Opening section in an iFrame...' );

        // If this section has a custom header
        updatePageTitle( sectionSpec );

        dispatch( activateLeftMenuSection( sectionSpec, updateTimeStamp ) );

        if ( pushUrl )
        {
            dispatch( focusSectionFrame( sectionSpec, null ) );
            dispatch( push( sectionSpec.externalUrl ) );
        }
    }
    else if ( sectionSpec.sectionUrlType === URL_TYPE_NEW_TAB )
    {
        Logger.debug( TAG, 'Opening section in a new tab' );

        window.open( sectionSpec.externalUrl, '_blank' );

    }
    else if ( sectionSpec.sectionUrlType === URL_TYPE_SAME_TAB )
    {
        Logger.debug( TAG, 'Opening section in the same tab' );

        window.location.href = sectionSpec.externalUrl;
    }
    else if ( sectionSpec.sectionUrlType === URL_TYPE_NEW_WINDOW )
    {
        Logger.debug( TAG, 'Opening section in the same tab' );

        window.open( sectionSpec.externalUrl );
    }
    else if ( sectionSpec.sectionUrlType === URL_TYPE_DEBUG_INFO )
    {
        const mainNavigationState: MainNavigationState = getState().mainNavigation;
        const isOpen = mainNavigationState.debugInfoState.isOpen;

        Logger.debug( TAG, 'Opening debug info section' );

        dispatch( updateDebugInfoOpenState( !isOpen ) );
    }
    else
    {
        Logger.warn( TAG, 'Unknown URL type: ' + sectionSpec.sectionUrlType );
    }
};

export const activateLeftMenuSection = ( sectionSpec: MenuSection, updateTimeStamp: boolean ) => ( dispatch, getState ) =>
{
    // Maybe close the menu
    const mainNavigationState: MainNavigationState = getState().mainNavigation;

    if ( DeviceDetectionUtils.isMobileDevice() && !mainNavigationState.leftMenuState.isPinnedOpen )
    {
        dispatch( updateSideLeftMenuOpenState( false ) );
    }

    if ( updateTimeStamp )
    {
        const lastLoadTimeStamp: string = DateTimeUtils.formatDate( DateTimeUtils.getUtcNow(), 'yyyyMMddhhss' );

        dispatch( {
            type: constants.ACTION_UPDATE_SIDE_MENU_TIME_STAMP_STATE,
            lastLoadTimeStamp: lastLoadTimeStamp
        } )
    }

    // Dispatch activation info
    dispatch(
        {
            type: constants.ACTION_ACTIVATE_LEFT_MENU_SECTION,
            sectionSpec: sectionSpec
        }
    );
};
